import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { IconManager } from '../../services/icon-manager.service';
import { iconMore } from '../../svg-icons';
import {
  MenuItem,
  MenuItemIconPosition,
} from '../../interfaces/common.interface';
import { stopPropagationEvent } from '../../common/utils';
import { MatDividerModule } from '@angular/material/divider';
import { PermissionSingleOrEvery } from '../../directives/permissions.directive';
import { PermissionService } from '../../services/permission.service';

export enum ItemMenuStyle {
  BUTTON = 'button',
  ICON = 'icon',
}

@Component({
  selector: 'twc-item-menu',
  imports: [
    CommonModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    PermissionSingleOrEvery,
  ],
  templateUrl: './item-menu.component.html',
  styleUrls: ['./item-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemMenuComponent<E> {
  private _iconManagerService = inject(IconManager);
  private _permissionService = inject(PermissionService);

  actions = input.required<MenuItem<E>[]>();
  actionArgument = input<E>(null);
  itemMenuStyle = input<ItemMenuStyle>(ItemMenuStyle.BUTTON);

  protected readonly stopPropagationEvent = stopPropagationEvent;
  protected readonly TableActionEntityIconPosition = MenuItemIconPosition;
  protected readonly ItemMenuStyle = ItemMenuStyle;
  protected readonly Array = Array;

  allOptionsHidden = computed(() => {
    const results = this.actions().map((action) => {
      const hidden = action.hidden?.(this.actionArgument()) ?? false;
      const hasPermission =
        !action.permission ||
        this._permissionService.checkSingleOrEveryPermission(action.permission);

      return {
        action,
        visible: !hidden && hasPermission,
      };
    });

    return results.every((action) => !action.visible);
  });

  items = computed(() => {
    const groupDividers = {};
    const newActions = this.actions().map((action) => {
      if (!action?.group) {
        return action;
      } else {
        /**
         * Add divider to the first visible action in each group
         */
        const hidden = action.hidden?.(this.actionArgument()) ?? false;
        const hasPermission =
          !action.permission ||
          this._permissionService.checkSingleOrEveryPermission(
            action.permission
          );

        if (!hidden && hasPermission && !groupDividers[action.group]) {
          groupDividers[action.group] = true;
          return { ...action, divider: true, visible: true };
        }

        return action;
      }
    });

    const foundIndex = newActions.findIndex((action) => action['visible']);

    /**
     * Hide first visible divider, we don't want to show divider at the first visible element
     */
    if (foundIndex !== -1) {
      newActions[foundIndex].divider = false;
    }

    return newActions;
  });

  constructor() {
    this._iconManagerService.addIcons([{ name: 'more', icon: iconMore }]);
  }
}
